import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UnifiedAuthProvider, useAuth } from './context/UnifiedAuthContext.jsx';
import { ToastProvider } from './contexts/ToastContext.jsx';
import Dashboard from './pages/Dashboard';
import PromptPlayground from "./components/promptPlayground/PromptPlayground.jsx";
import Profile from "./components/Profile/Profile.jsx";
import PersonaBuilder from "./components/PersonaBuilder/PersonaBuilder.jsx";
import BankAccount from "./components/BankAccount/BankAccount.jsx";
import RedeemRewards from "./components/RedeemRewards/RedeemRewards.jsx";
import AdminPortal from "./components/AdminPortal/AdminPortal.jsx";
import Login from "./components/Login/Login.jsx";
import PortalLoginComplete from "./components/Login/PortalLoginComplete.jsx";
import PortalLoginAcknowledgement from "./components/Login/PortalLoginAcknowledgement.jsx";
import GHLConfig from "./pages/GHLConfig/GHLConfig.jsx";
import './App.css';
import './styles/main.scss';
import LoadingSpinner from "./components/LoadingSpinner/LoadingSpinner.jsx";

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { technician, loading, error, urlParams, invalidateSession } = useAuth();
    const routeLocation = useLocation();
    const currentParams = new URLSearchParams(routeLocation.search);
    const visibleLocation = currentParams.get('location');
    const visibleUser = currentParams.get('user');
    const authenticatedLocation = urlParams.location;
    const authenticatedUser = urlParams.user;
    const hasContextMismatch = Boolean(technician) && (
        visibleLocation !== authenticatedLocation
        || visibleUser !== authenticatedUser
    );

    useEffect(() => {
        if (!loading && hasContextMismatch) invalidateSession();
    }, [hasContextMismatch, invalidateSession, loading]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error || !technician) {
        const loginParams = new URLSearchParams();
        const location = currentParams.get('location');
        const user = currentParams.get('user');
        const integration = currentParams.get('integration');

        if (location) loginParams.set('location', location);
        if (user) loginParams.set('user', user);
        if (integration) loginParams.set('integration', integration);

        const search = loginParams.toString();
        return (
            <Navigate
                to={`/login${search ? `?${search}` : ''}`}
                replace
                state={{ authError: error || undefined }}
            />
        );
    }

    // URL values are routing context, never authorization. Do not silently
    // replace a caller-supplied tenant/user with values from a different signed
    // session: send them back through authentication. Direct page loads and all
    // API calls are independently rejected by the backend on the same mismatch.
    if (hasContextMismatch) {
        return (
            <Navigate
                to={`/login?${currentParams.toString()}`}
                replace
                state={{
                    authError: 'The location or user in this URL does not match your authenticated session.'
                }}
            />
        );
    }

    return children;
};

// App Content with Routes
const AppContent = () => {
    return (
        <div className="app">
            <PortalLoginAcknowledgement />
            <Routes>
                {/* Public Google sign-in and invitation entry route */}
                <Route path="/login" element={<Login />} />
                <Route
                    path="/login/complete"
                    element={<ProtectedRoute><PortalLoginComplete /></ProtectedRoute>}
                />

                {/* Main Dashboard Route */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                />

                {/* Dashboard Route */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                />

                {/* Password auth is retired; Google account security is managed by Google. */}
                <Route path="/change-password" element={<DashboardRedirect />} />

                {/* Profile Route */}
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />

                {/* Persona Builder Route */}
                <Route
                    path="/persona-builder"
                    element={
                        <ProtectedRoute>
                            <PersonaBuilder />
                        </ProtectedRoute>
                    }
                />

                {/* Bank Account Route */}
                <Route
                    path="/bank-account"
                    element={
                        <ProtectedRoute>
                            <BankAccount />
                        </ProtectedRoute>
                    }
                />

                {/* Redeem Rewards Route */}
                <Route
                    path="/redeem-rewards"
                    element={
                        <ProtectedRoute>
                            <RedeemRewards />
                        </ProtectedRoute>
                    }
                />

                {/* Admin Prompts Route */}
                <Route
                    path="/admin/prompts"
                    element={
                        <ProtectedRoute>
                            <PromptPlayground />
                        </ProtectedRoute>
                    }
                />

                {/* Admin Portal Route */}
                <Route
                    path="/admin/portal"
                    element={
                        <ProtectedRoute>
                            <AdminPortal />
                        </ProtectedRoute>
                    }
                />

                {/* Tenant integration configuration is restricted to an authenticated user. */}
                <Route
                    path="/config"
                    element={
                        <ProtectedRoute>
                            <GHLConfig />
                        </ProtectedRoute>
                    }
                />

                {/* Catch all route - redirect to dashboard */}
                <Route path="*" element={<DashboardRedirect />} />
            </Routes>
        </div>
    );
};

const DashboardRedirect = () => {
    const location = useLocation();
    return <Navigate to={{ pathname: '/dashboard', search: location.search }} replace />;
};

// Main App Component
function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <UnifiedAuthProvider>
                <ToastProvider>
                    <Router>
                        <AppContent />
                    </Router>
                </ToastProvider>
            </UnifiedAuthProvider>
        </QueryClientProvider>
    );
}

export default App;
